// Chakra Imports
import {
  Box,
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  Button,
  Flex,
  Link,
  Text,
  useColorModeValue,
  Icon,
  HStack,
} from "@chakra-ui/react";
import { FaPlus, FaHome, FaChevronRight } from "react-icons/fa";
import PropTypes from "prop-types";
import React, { useState, useEffect } from "react";
import AdminNavbarLinks from "components/navbar/NavbarLinksAdmin";
// FaPlus is now imported above
import { useLocation, useNavigate } from "react-router-dom";
import { useRecoilValue, useSetRecoilState } from "recoil";
import { createUserDrawerState } from "atoms/createUserDrawerState";
import { createRfidDrawerState } from "atoms/createRfidDrawerState";
import { createPropertyOwnerDrawerState } from "atoms/createPropertyOwnerState";
import logger from "infra/logger";
import { loggedInUserState } from "atoms/loggedInUserState";
import { accessLevels } from "utils/accessLevels";
import { createPartnerDrawerState } from "atoms/createPartnerDrawerState";
import { createTenantDrawerState } from "atoms/createTenantDrawerState";
import { createUserProfileDrawerState } from "atoms/createUserProfileDrawerState";
import RoleBasedRender from "components/wrappers/roleBasedRender";
import { onlyAdmins } from "utils/accessLevels";
import { onlyUAdmins } from "utils/accessLevels";

export default function AdminNavbar(props) {
  const loggedInUser = useRecoilValue(loggedInUserState);
  const role = loggedInUser?.role;
  const canEdit = accessLevels[role]?.accessLevel <= 2;
  const [scrolled, setScrolled] = useState(false);
  const navigate = useNavigate();
  const setUserDrawerState = useSetRecoilState(createUserDrawerState);
  const setRfidDrawerState = useSetRecoilState(createRfidDrawerState);
  const setPropertyOwnerDrawerState = useSetRecoilState(
    createPropertyOwnerDrawerState
  );
  const setPartnerDrawerState = useSetRecoilState(createPartnerDrawerState);
  const setTenantDrawerState = useSetRecoilState(createTenantDrawerState);
  const setUserProfileDrawerState = useSetRecoilState(createUserProfileDrawerState);
  useEffect(() => {
    window.addEventListener("scroll", changeNavbar);
    return () => {
      window.removeEventListener("scroll", changeNavbar);
    };
  });

  const { secondary, message, brandText } = props;

  // Here are all the props that may change depending on navbar's type or state.(secondary, variant, scrolled)
  let mainText = useColorModeValue("navy.700", "white");
  let secondaryText = useColorModeValue("gray.700", "white");
  let navbarPosition = "fixed";
  let navbarFilter = "none";
  let navbarBackdrop = "blur(20px)";
  let navbarShadow = "none";
  let navbarBg = useColorModeValue(
    "rgba(244, 247, 254, 0.2)",
    "rgba(11,20,55,0.5)"
  );
  let navbarBorder = "transparent";
  let secondaryMargin = "0px";
  let paddingX = "15px";
  let gap = "0px";
  const changeNavbar = () => {
    if (window.scrollY > 1) {
      setScrolled(true);
    } else {
      setScrolled(false);
    }
  };

  const location = useLocation();

  const isChargingStationsRoute = location.pathname === "/admin/locations";
  const isUserRoute = location.pathname === "/admin/users/list";
  const isRfidRoute = location.pathname === "/admin/rfid_cards/list";
  const isContractsRoute = location.pathname === "/admin/contracts/list";
  const isUserProfileRoute = location.pathname === "/admin/userprofiles/list";
  const isPropertyOwnerRoute =
    location.pathname === "/admin/propertyowners/list";
  const isPartnersRoute = location.pathname === "/admin/partners/list";
  const isTenantsRoute = location.pathname === "/admin/tenants/list";

  return (
    <Box
      position={navbarPosition}
      boxShadow={navbarShadow}
      bg={navbarBg}
      borderColor={navbarBorder}
      filter={navbarFilter}
      backdropFilter={navbarBackdrop}
      backgroundPosition="center"
      backgroundSize="cover"
      borderRadius="16px"
      borderWidth="1.5px"
      borderStyle="solid"
      transitionDelay="0s, 0s, 0s, 0s"
      transitionDuration=" 0.25s, 0.25s, 0.25s, 0s"
      transition-property="box-shadow, background-color, filter, border"
      transitionTimingFunction="linear, linear, linear, linear"
      alignItems={{ xl: "center" }}
      display={secondary ? "block" : "flex"}
      minH="75px"
      justifyContent={{ xl: "center" }}
      lineHeight="25.6px"
      mx="auto"
      mt={secondaryMargin}
      pb="8px"
      right={{ base: "8px", sm: "12px", md: "30px", lg: "30px", xl: "30px" }}
      px={{
        base: "8px",
        sm: paddingX,
        md: "10px",
      }}
      ps={{
        xl: "12px",
      }}
      pt="8px"
      w={{
        base: "calc(100vw - 16px)",
        sm: "calc(100vw - 6%)",
        md: "calc(100vw - 8%)",
        lg: "calc(100vw - 6%)",
        xl: "calc(100vw - 350px)",
        "2xl": "calc(100vw - 365px)",
      }}
    >
      <Flex
        w="100%"
        display={"flex"}
        flexDirection={{
          base: "column",
          sm: "column",
          md: "row",
        }}
        alignItems={{ base: "flex-start", md: "center", xl: "center" }}
        justifyContent={"space-between"}
        mb={gap}
        gap={{ base: "8px", md: "0" }}
      >
        <Box mb={{ base: "8px", sm: "8px", md: "0px" }} w={{ base: "100%", md: "auto" }}>
          <Breadcrumb
            separator={<Icon as={FaChevronRight} color="brand.500" boxSize={3} />}
            spacing={{ base: 1, sm: 2 }}
          >
            <BreadcrumbItem>
              <BreadcrumbLink
                href="/admin/dashboard/overview"
                bg="rgba(255, 255, 255, 0.6)"
                backdropFilter="blur(10px)"
                borderRadius="lg"
                px={{ base: 2, sm: 3 }}
                py={{ base: 1, sm: 1.5 }}
                fontSize={{ base: "xs", sm: "sm" }}
                fontWeight="500"
                border="1px solid rgba(35, 169, 121, 0.15)"
                color="gray.600"
                _hover={{
                  bg: "rgba(35, 169, 121, 0.08)",
                  color: "brand.500",
                  textDecoration: "none",
                  boxShadow: "0 2px 5px rgba(35, 169, 121, 0.1)",
                }}
                transition="all 0.2s ease"
              >
                <HStack spacing={{ base: 1, sm: 1.5 }}>
                  <Icon as={FaHome} color="brand.500" boxSize={{ base: 3, sm: 3.5 }} />
                  <Text display={{ base: "none", sm: "block" }}>Admin</Text>
                </HStack>
              </BreadcrumbLink>
            </BreadcrumbItem>

            <BreadcrumbItem isCurrentPage>
              <BreadcrumbLink
                href="#"
                bg="rgba(35, 169, 121, 0.15)"
                backdropFilter="blur(10px)"
                borderRadius="lg"
                px={{ base: 2, sm: 3 }}
                py={{ base: 1, sm: 1.5 }}
                fontSize={{ base: "xs", sm: "sm" }}
                fontWeight="600"
                color="brand.500"
                border="1px solid rgba(35, 169, 121, 0.25)"
                boxShadow="0 2px 4px rgba(35, 169, 121, 0.1)"
                _hover={{
                  bg: "rgba(35, 169, 121, 0.2)",
                  textDecoration: "none",
                }}
                transition="all 0.2s ease"
                maxW={{ base: "150px", sm: "200px", md: "none" }}
                overflow="hidden"
                textOverflow="ellipsis"
                whiteSpace="nowrap"
              >
                {brandText}
              </BreadcrumbLink>
            </BreadcrumbItem>
          </Breadcrumb>
        </Box>

        {isChargingStationsRoute && (
          <RoleBasedRender roles={onlyAdmins}>
            <Button
              leftIcon={<FaPlus />}
              ms={{ base: "0", md: "auto" }}
              me={{ base: "0", md: 10 }}
              w={{ base: "100%", md: "auto" }}
              bg="rgba(35, 169, 121, 0.9)"
              color="white"
              borderRadius="lg"
              _hover={{
                bg: "rgba(35, 169, 121, 1)",
                boxShadow: "0 4px 8px rgba(35, 169, 121, 0.25)",
                transform: "translateY(-2px)",
              }}
              _active={{
                bg: "rgba(225, 105, 34, 1)",
                transform: "translateY(0)",
              }}
              boxShadow="0 2px 5px rgba(35, 169, 121, 0.2)"
              backdropFilter="blur(10px)"
              transition="all 0.2s ease"
              fontWeight="500"
              px={{ base: 4, md: 5 }}
              fontSize={{ base: "sm", md: "md" }}
              onClick={() => navigate("/admin/locations/new")}
              disabled={!canEdit}
              _disabled={{
                opacity: 0.6,
                cursor: "not-allowed",
                boxShadow: "none",
                bg: "rgba(35, 169, 121, 0.5)",
              }}
            >
              ADD LOCATION
            </Button>
          </RoleBasedRender>
        )}
        {isUserRoute && (
          <RoleBasedRender roles={onlyAdmins}>
            <Button
              leftIcon={<FaPlus />}
              ms={{ base: "0", md: "auto" }}
              me={{ base: "0", md: 10 }}
              w={{ base: "100%", md: "auto" }}
              bg="rgba(35, 169, 121, 0.9)"
              color="white"
              borderRadius="lg"
              _hover={{
                bg: "rgba(35, 169, 121, 1)",
                boxShadow: "0 4px 8px rgba(35, 169, 121, 0.25)",
                transform: "translateY(-2px)",
              }}
              _active={{
                bg: "rgba(225, 105, 34, 1)",
                transform: "translateY(0)",
              }}
              boxShadow="0 2px 5px rgba(35, 169, 121, 0.2)"
              backdropFilter="blur(10px)"
              transition="all 0.2s ease"
              fontWeight="500"
              px={{ base: 4, md: 5 }}
              fontSize={{ base: "sm", md: "md" }}
              onClick={() => setUserDrawerState({ isOpen: true })}
              disabled={!canEdit}
              _disabled={{
                opacity: 0.6,
                cursor: "not-allowed",
                boxShadow: "none",
                bg: "rgba(35, 169, 121, 0.5)",
              }}
            >
              ADD USER
            </Button>
          </RoleBasedRender>
        )}
        {isRfidRoute && (
          <RoleBasedRender roles={onlyAdmins}>
            <Button
              leftIcon={<FaPlus />}
              ms={{ base: "0", md: "auto" }}
              me={{ base: "0", md: 10 }}
              w={{ base: "100%", md: "auto" }}
              bg="rgba(35, 169, 121, 0.9)"
              color="white"
              borderRadius="lg"
              _hover={{
                bg: "rgba(35, 169, 121, 1)",
                boxShadow: "0 4px 8px rgba(35, 169, 121, 0.25)",
                transform: "translateY(-2px)",
              }}
              _active={{
                bg: "rgba(225, 105, 34, 1)",
                transform: "translateY(0)",
              }}
              boxShadow="0 2px 5px rgba(35, 169, 121, 0.2)"
              backdropFilter="blur(10px)"
              transition="all 0.2s ease"
              fontWeight="500"
              px={{ base: 4, md: 5 }}
              fontSize={{ base: "sm", md: "md" }}
              onClick={() => setRfidDrawerState({ isOpen: true })}
              disabled={!canEdit}
              _disabled={{
                opacity: 0.6,
                cursor: "not-allowed",
                boxShadow: "none",
                bg: "rgba(35, 169, 121, 0.5)",
              }}
            >
              GENERATE RFID CARD
            </Button>
          </RoleBasedRender>
        )}
        {isContractsRoute && (
          <RoleBasedRender roles={onlyAdmins}>
            <Button
              leftIcon={<FaPlus />}
              ms={{ base: "0", md: "auto" }}
              me={{ base: "0", md: 10 }}
              w={{ base: "100%", md: "auto" }}
              bg="rgba(35, 169, 121, 0.9)"
              color="white"
              borderRadius="lg"
              _hover={{
                bg: "rgba(35, 169, 121, 1)",
                boxShadow: "0 4px 8px rgba(35, 169, 121, 0.25)",
                transform: "translateY(-2px)",
              }}
              _active={{
                bg: "rgba(225, 105, 34, 1)",
                transform: "translateY(0)",
              }}
              boxShadow="0 2px 5px rgba(35, 169, 121, 0.2)"
              backdropFilter="blur(10px)"
              transition="all 0.2s ease"
              fontWeight="500"
              px={{ base: 4, md: 5 }}
              fontSize={{ base: "sm", md: "md" }}
              onClick={() => navigate("/admin/contracts/new")}
              disabled={!canEdit}
              _disabled={{
                opacity: 0.6,
                cursor: "not-allowed",
                boxShadow: "none",
                bg: "rgba(35, 169, 121, 0.5)",
              }}
            >
              CREATE CONTRACT
            </Button>
          </RoleBasedRender>
        )}
        {isUserProfileRoute && (
          <RoleBasedRender roles={onlyAdmins}>
            <Button
              leftIcon={<FaPlus />}
              ms={{ base: "0", md: "auto" }}
              me={{ base: "0", md: 10 }}
              w={{ base: "100%", md: "auto" }}
              bg="rgba(35, 169, 121, 0.9)"
              color="white"
              borderRadius="lg"
              _hover={{
                bg: "rgba(35, 169, 121, 1)",
                boxShadow: "0 4px 8px rgba(35, 169, 121, 0.25)",
                transform: "translateY(-2px)",
              }}
              _active={{
                bg: "rgba(225, 105, 34, 1)",
                transform: "translateY(0)",
              }}
              boxShadow="0 2px 5px rgba(35, 169, 121, 0.2)"
              backdropFilter="blur(10px)"
              transition="all 0.2s ease"
              fontWeight="500"
              px={{ base: 4, md: 5 }}
              fontSize={{ base: "sm", md: "md" }}
              onClick={() => setUserProfileDrawerState({ isOpen: true })}
              disabled={!canEdit}
              _disabled={{
                opacity: 0.6,
                cursor: "not-allowed",
                boxShadow: "none",
                bg: "rgba(35, 169, 121, 0.5)",
              }}
            >
              CREATE GROUP
            </Button>
          </RoleBasedRender>
        )}
        {isPropertyOwnerRoute && (
          <RoleBasedRender roles={onlyAdmins}>
            <Button
              leftIcon={<FaPlus />}
              ms={{ base: "0", md: "auto" }}
              me={{ base: "0", md: 10 }}
              w={{ base: "100%", md: "auto" }}
              bg="rgba(35, 169, 121, 0.9)"
              color="white"
              borderRadius="lg"
              _hover={{
                bg: "rgba(35, 169, 121, 1)",
                boxShadow: "0 4px 8px rgba(35, 169, 121, 0.25)",
                transform: "translateY(-2px)",
              }}
              _active={{
                bg: "rgba(225, 105, 34, 1)",
                transform: "translateY(0)",
              }}
              boxShadow="0 2px 5px rgba(35, 169, 121, 0.2)"
              backdropFilter="blur(10px)"
              transition="all 0.2s ease"
              fontWeight="500"
              px={{ base: 4, md: 5 }}
              fontSize={{ base: "sm", md: "md" }}
              onClick={() => setPropertyOwnerDrawerState({ isOpen: true })}
              disabled={!canEdit}
              _disabled={{
                opacity: 0.6,
                cursor: "not-allowed",
                boxShadow: "none",
                bg: "rgba(35, 169, 121, 0.5)",
              }}
            >
              ADD PROPERTY OWNER
            </Button>
          </RoleBasedRender>
        )}
        {isPartnersRoute && (
          <RoleBasedRender roles={onlyUAdmins}>
            <Button
              leftIcon={<FaPlus />}
              ms={{ base: "0", md: "auto" }}
              me={{ base: "0", md: 10 }}
              w={{ base: "100%", md: "auto" }}
              bg="rgba(35, 169, 121, 0.9)"
              color="white"
              borderRadius="lg"
              _hover={{
                bg: "rgba(35, 169, 121, 1)",
                boxShadow: "0 4px 8px rgba(35, 169, 121, 0.25)",
                transform: "translateY(-2px)",
              }}
              _active={{
                bg: "rgba(225, 105, 34, 1)",
                transform: "translateY(0)",
              }}
              boxShadow="0 2px 5px rgba(35, 169, 121, 0.2)"
              backdropFilter="blur(10px)"
              transition="all 0.2s ease"
              fontWeight="500"
              px={{ base: 4, md: 5 }}
              fontSize={{ base: "sm", md: "md" }}
              onClick={() => setPartnerDrawerState({ isOpen: true })}
              disabled={!canEdit}
              _disabled={{
                opacity: 0.6,
                cursor: "not-allowed",
                boxShadow: "none",
                bg: "rgba(35, 169, 121, 0.5)",
              }}
            >
              ADD PARTNER
            </Button>
          </RoleBasedRender>
        )}
        {isTenantsRoute && (
          <RoleBasedRender roles={onlyUAdmins}>
            <Button
              leftIcon={<FaPlus />}
              ms={{ base: "0", md: "auto" }}
              me={{ base: "0", md: 10 }}
              w={{ base: "100%", md: "auto" }}
              bg="rgba(35, 169, 121, 0.9)"
              color="white"
              borderRadius="lg"
              _hover={{
                bg: "rgba(35, 169, 121, 1)",
                boxShadow: "0 4px 8px rgba(35, 169, 121, 0.25)",
                transform: "translateY(-2px)",
              }}
              _active={{
                bg: "rgba(225, 105, 34, 1)",
                transform: "translateY(0)",
              }}
              boxShadow="0 2px 5px rgba(35, 169, 121, 0.2)"
              backdropFilter="blur(10px)"
              transition="all 0.2s ease"
              fontWeight="500"
              px={{ base: 4, md: 5 }}
              fontSize={{ base: "sm", md: "md" }}
              onClick={() => setTenantDrawerState({ isOpen: true })}
              disabled={!canEdit}
              _disabled={{
                opacity: 0.6,
                cursor: "not-allowed",
                boxShadow: "none",
                bg: "rgba(35, 169, 121, 0.5)",
              }}
            >
              ADD TENANT
            </Button>
          </RoleBasedRender>
        )}

        <Box w={{ base: "100%", sm: "100%", md: "unset" }}>
          <AdminNavbarLinks
            onOpen={props.onOpen}
            logoText={props.logoText}
            secondary={props.secondary}
            fixed={props.fixed}
            scrolled={scrolled}
          />
        </Box>
      </Flex>
      {secondary ? <Text color="white">{message}</Text> : null}
    </Box>
  );
}

AdminNavbar.propTypes = {
  brandText: PropTypes.string,
  variant: PropTypes.string,
  secondary: PropTypes.bool,
  fixed: PropTypes.bool,
  onOpen: PropTypes.func,
};
